import { type Dayjs } from 'dayjs';
import { Suspense, type ReactElement, useMemo, useState } from 'react';
import { BuildsTable } from '@/features/builds/components/BuildsTable';
import { JobsTable } from '@/features/jobs/components/JobsTable';
import { resolveStartedAtFilterBounds } from '@/features/jobs/components/jobsTableDateFilters';
import { queryJobRuns, queryJobs } from '@/features/jobs/data/jobs';
import { buildJobRunDetailPath } from '@/features/jobs/utils/jobPaths';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { DeferredTablePanelSkeleton } from '@/features/shared/components/deferred/DeferredSectionSkeletons';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  tablePanelTabSx,
  tablePanelTabsSx,
} from '@/features/shared/components/tableSystem/tablePanelTabs';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { useDateWindow, type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { gridPaths, queryKeys } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildLocalQueryResult } from '@/ui/composites/grid/localGridUtils';
import { Panel } from '@/ui/composites/Panel';
import { Box, type GridColumnVisibilityModel, Tab, Tabs } from '@/ui/primitives';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { BuildDto, BuildStatus, JobListItemDto, QueryResult } from '@flowqa/shared';

type JobsPanelTab = 'jobs' | 'runs';
export type JobSortKey =
  | 'pipelineName'
  | 'stageName'
  | 'jobName'
  | 'status'
  | 'runTotal'
  | 'runFrequencyPerDay'
  | 'runAverageDurationMs'
  | 'runPassRate'
  | 'startedAt';
export type RunSortKey =
  | 'startedAt'
  | 'finishedAt'
  | 'status'
  | 'pipelineName'
  | 'stageName'
  | 'jobName';

const jobSortFieldMap: Record<JobSortKey, string> = {
  pipelineName: 'pipelineName',
  stageName: 'stageName',
  jobName: 'jobName',
  status: 'status',
  runTotal: 'runTotal',
  runFrequencyPerDay: 'runFrequencyPerDay',
  runAverageDurationMs: 'runAverageDurationMs',
  runPassRate: 'runPassRate',
  startedAt: 'startedAt',
};

const runSortFieldMap: Record<RunSortKey, string> = {
  startedAt: 'startedAt',
  finishedAt: 'finishedAt',
  status: 'status',
  pipelineName: 'pipelineName',
  stageName: 'stageName',
  jobName: 'jobName',
};

const jobQuickSortOptions: Array<{
  key: JobSortKey;
  label: string;
  metric: 'passRate' | 'frequency' | 'avgDuration';
}> = [
  { key: 'runPassRate', label: getChartTitle('passRate'), metric: 'passRate' },
  { key: 'runFrequencyPerDay', label: getChartTitle('frequency'), metric: 'frequency' },
  { key: 'runAverageDurationMs', label: getChartTitle('avgDuration'), metric: 'avgDuration' },
];

const jobDefaultColumnVisibilityModel: GridColumnVisibilityModel = {
  jobName: true,
  runTotal: true,
  status: true,
  metricsSummary: true,
};

const runDefaultColumnVisibilityModel: GridColumnVisibilityModel = {
  jobName: true,
  buildKey: true,
  status: true,
  stageStatusCounts: false,
};

const filterJobRunRowsBySearch = (rows: BuildDto[], searchValue: string): BuildDto[] => {
  const normalizedSearch = searchValue.trim().toLowerCase();
  if (normalizedSearch.length === 0) {
    return rows;
  }

  return rows.filter((row) =>
    [
      row.pipelineName ?? '',
      row.stageName ?? '',
      row.jobName ?? '',
      row.buildKey ?? '',
      row.status,
      ...(row.tags ?? []),
    ].some((value) => value.toLowerCase().includes(normalizedSearch)),
  );
};

export type JobsTablePanelProps = {
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  tagOptions: TagOption[];
  pipelineOptions: TableDropdownOption[];
  stageOptions: TableDropdownOption[];
  jobOptions: TableDropdownOption[];
  activeTab: JobsPanelTab;
  onActiveTabChange: (nextTab: JobsPanelTab) => void;
  dateQuickFilter: DateQuickFilter;
  onDateQuickFilterChange: (next: DateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  latestQueryStartDay: string | null;
  latestQueryEndDay: string | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
  jobSearchValue: string;
  onJobSearchChange: (value: string) => void;
  jobColumnVisibilityModel: GridColumnVisibilityModel;
  onJobColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  jobSortKey: JobSortKey;
  jobSortDirection: 'asc' | 'desc';
  onJobSortChange: (nextSortKey: JobSortKey, nextSortDirection: 'asc' | 'desc') => void;
  runSearchValue: string;
  onRunSearchChange: (value: string) => void;
  runColumnVisibilityModel: GridColumnVisibilityModel;
  onRunColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  runSortKey: RunSortKey;
  runSortDirection: 'asc' | 'desc';
  onRunSortChange: (nextSortKey: RunSortKey, nextSortDirection: 'asc' | 'desc') => void;
  filterStatuses: BuildStatus[];
  onFilterStatusesChange: (next: BuildStatus[]) => void;
  filterPipelineIds: string[];
  onFilterPipelineIdsChange: (next: string[]) => void;
  filterStageNames: string[];
  onFilterStageNamesChange: (next: string[]) => void;
  filterJobNames: string[];
  onFilterJobNamesChange: (next: string[]) => void;
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  jobSummaryCount: number | null;
  runSummaryCount: number | null;
  runRows?: BuildDto[];
};

export const JobsTablePanel = ({
  selectedProjectId,
  selectedOrganisationId,
  tagOptions,
  pipelineOptions,
  stageOptions,
  jobOptions,
  activeTab,
  onActiveTabChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  latestQueryStartDay,
  latestQueryEndDay,
  onFromDateChange,
  onToDateChange,
  jobSearchValue,
  onJobSearchChange,
  jobColumnVisibilityModel,
  onJobColumnVisibilityModelChange,
  jobSortKey,
  jobSortDirection,
  onJobSortChange,
  runSearchValue,
  onRunSearchChange,
  runColumnVisibilityModel,
  onRunColumnVisibilityModelChange,
  runSortKey,
  runSortDirection,
  onRunSortChange,
  filterStatuses,
  onFilterStatusesChange,
  filterPipelineIds,
  onFilterPipelineIdsChange,
  filterStageNames,
  onFilterStageNamesChange,
  filterJobNames,
  onFilterJobNamesChange,
  filterTags,
  onFilterTagsChange,
  jobSummaryCount,
  runSummaryCount,
  runRows = [],
}: JobsTablePanelProps): ReactElement => {
  const [jobMeta, setJobMeta] = useState<TableQueryMeta<JobListItemDto> | null>(null);
  const [runMeta, setRunMeta] = useState<TableQueryMeta<BuildDto> | null>(null);
  const availableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const dateWindow = useDateWindow({
    dateQuickFilter,
    fromDate,
    toDate,
    latestQueryStartDay,
    latestQueryEndDay,
  });
  const startedAtFilterBounds = useMemo(
    () =>
      resolveStartedAtFilterBounds({
        startDay: dateWindow.startDay,
        endDay: dateWindow.endDay,
      }),
    [dateWindow.endDay, dateWindow.startDay],
  );
  const statusFilterKey = useMemo(
    () => [...filterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [filterStatuses],
  );
  const tagFilterKey = useMemo(
    () => [...filterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [filterTags],
  );
  const pipelineFilterKey = useMemo(
    () => [...filterPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
    [filterPipelineIds],
  );
  const stageFilterKey = useMemo(
    () => [...filterStageNames].sort((left, right) => left.localeCompare(right)).join(','),
    [filterStageNames],
  );

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
            backgroundImage: 'none',
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Box
            sx={{
              backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
              backgroundImage: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'md',
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={activeTab}
                onChange={(_event, next) => {
                  onActiveTabChange(next as JobsPanelTab);
                }}
                aria-label="Jobs table tabs"
                sx={tablePanelTabsSx}
              >
                <Tab label="Jobs" value="jobs" sx={tablePanelTabSx} />
                <Tab label="Runs" value="runs" sx={tablePanelTabSx} />
              </Tabs>
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
              {activeTab === 'jobs' ? (
                <TableToolbar<JobSortKey, BuildStatus>
                  title={undefined}
                  searchAriaLabel="Search jobs"
                  searchValue={jobSearchValue}
                  onDebouncedSearchChange={onJobSearchChange}
                  columnVisibilityModel={jobColumnVisibilityModel}
                  defaultColumnVisibilityModel={jobDefaultColumnVisibilityModel}
                  columnOptions={[
                    { field: 'jobName', label: 'Job', mandatory: true },
                    { field: 'runTotal', label: 'Runs' },
                    { field: 'status', label: getChartTitle('latestStatus') },
                    { field: 'metricsSummary', label: getDetailLabel('metrics') },
                  ]}
                  onColumnVisibilityModelChange={onJobColumnVisibilityModelChange}
                  sortKey={jobSortKey}
                  sortDirection={jobSortDirection}
                  defaultSortKey="jobName"
                  defaultSortDirection="asc"
                  sortOptions={[
                    { key: 'pipelineName', label: getDetailLabel('pipeline') },
                    { key: 'stageName', label: getDetailLabel('stage') },
                    { key: 'jobName', label: 'Job' },
                    { key: 'status', label: getDetailLabel('status') },
                    { key: 'runTotal', label: 'Runs' },
                    { key: 'runFrequencyPerDay', label: getChartTitle('frequency') },
                    { key: 'runAverageDurationMs', label: getChartTitle('avgDuration') },
                    { key: 'runPassRate', label: getChartTitle('passRate') },
                    { key: 'startedAt', label: 'Latest' },
                  ]}
                  quickSortOptions={jobQuickSortOptions}
                  onSortChange={onJobSortChange}
                  filterStatuses={filterStatuses}
                  statusOptions={buildStatusOptions}
                  onFilterStatusesChange={onFilterStatusesChange}
                  filterEntities={filterStageNames}
                  onFilterEntitiesChange={onFilterStageNamesChange}
                  entityOptions={stageOptions}
                  entityLabel="Stage"
                  filterItems={filterJobNames}
                  onFilterItemsChange={onFilterJobNamesChange}
                  itemOptions={jobOptions}
                  itemLabel="Job"
                  filterSuites={filterPipelineIds}
                  onFilterSuitesChange={onFilterPipelineIdsChange}
                  suiteOptions={pipelineOptions}
                  filterTags={filterTags}
                  onFilterTagsChange={onFilterTagsChange}
                  dateQuickFilter={dateQuickFilter}
                  onDateQuickFilterChange={onDateQuickFilterChange}
                  fromDate={fromDate}
                  toDate={toDate}
                  latestQueryStartDay={latestQueryStartDay}
                  latestQueryEndDay={latestQueryEndDay}
                  onFromDateChange={onFromDateChange}
                  onToDateChange={onToDateChange}
                  availableTags={availableTags}
                  summaryCount={jobSummaryCount ?? jobMeta?.total ?? null}
                  summarySingularLabel="job"
                  summaryPluralLabel="jobs"
                  hideDateRangeLabel
                  showControlsWithoutSummary
                />
              ) : (
                <TableToolbar<RunSortKey, BuildStatus>
                  title={undefined}
                  searchAriaLabel="Search job runs"
                  searchValue={runSearchValue}
                  onDebouncedSearchChange={onRunSearchChange}
                  columnVisibilityModel={runColumnVisibilityModel}
                  defaultColumnVisibilityModel={runDefaultColumnVisibilityModel}
                  columnOptions={[
                    { field: 'jobName', label: 'Jobs', mandatory: true },
                    { field: 'buildKey', label: getDetailLabel('run'), mandatory: true },
                    { field: 'status', label: getDetailLabel('status') },
                  ]}
                  onColumnVisibilityModelChange={onRunColumnVisibilityModelChange}
                  sortKey={runSortKey}
                  sortDirection={runSortDirection}
                  defaultSortKey="startedAt"
                  defaultSortDirection="desc"
                  sortOptions={[
                    { key: 'startedAt', label: getDetailLabel('started') },
                    { key: 'finishedAt', label: getDetailLabel('finished') },
                    { key: 'status', label: getDetailLabel('status') },
                    { key: 'pipelineName', label: getDetailLabel('pipeline') },
                    { key: 'stageName', label: getDetailLabel('stage') },
                    { key: 'jobName', label: 'Job' },
                  ]}
                  onSortChange={onRunSortChange}
                  filterStatuses={filterStatuses}
                  statusOptions={buildStatusOptions}
                  onFilterStatusesChange={onFilterStatusesChange}
                  filterEntities={filterStageNames}
                  onFilterEntitiesChange={onFilterStageNamesChange}
                  entityOptions={stageOptions}
                  entityLabel="Stage"
                  filterItems={filterJobNames}
                  onFilterItemsChange={onFilterJobNamesChange}
                  itemOptions={jobOptions}
                  itemLabel="Job"
                  filterSuites={filterPipelineIds}
                  onFilterSuitesChange={onFilterPipelineIdsChange}
                  suiteOptions={pipelineOptions}
                  filterTags={filterTags}
                  onFilterTagsChange={onFilterTagsChange}
                  dateQuickFilter={dateQuickFilter}
                  onDateQuickFilterChange={onDateQuickFilterChange}
                  fromDate={fromDate}
                  toDate={toDate}
                  latestQueryStartDay={latestQueryStartDay}
                  latestQueryEndDay={latestQueryEndDay}
                  onFromDateChange={onFromDateChange}
                  onToDateChange={onToDateChange}
                  availableTags={availableTags}
                  summaryCount={runSummaryCount ?? runMeta?.total ?? null}
                  summarySingularLabel="run"
                  summaryPluralLabel="runs"
                  hideDateRangeLabel
                  showControlsWithoutSummary
                />
              )}
            </Box>
          </Box>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      {activeTab === 'jobs' ? (
        <Suspense
          fallback={<DeferredTablePanelSkeleton embedded titleWidth={84} variant="builds" />}
        >
          <JobsTable
            queryFn={async (input: GridQueryInput): Promise<QueryResult<JobListItemDto>> =>
              await queryJobs(selectedProjectId, selectedOrganisationId ?? undefined, {
                ...input,
                search: jobSearchValue,
                sorts: [{ field: jobSortFieldMap[jobSortKey], direction: jobSortDirection }],
                filters: mergeFilters(
                  buildInFilter('status', filterStatuses),
                  buildInFilter('pipelineId', filterPipelineIds),
                  buildInFilter('stageName', filterStageNames),
                  buildInFilter('jobName', filterJobNames),
                  buildInFilter('tags', filterTags),
                  buildDateRangeFilters(
                    'startedAt',
                    startedAtFilterBounds.startAt,
                    startedAtFilterBounds.endAt,
                  ),
                ),
              })
            }
            queryKey={queryKeys.dataGrid(
              'jobs/table',
              selectedOrganisationId ?? null,
              selectedProjectId ?? null,
              jobSearchValue,
              jobSortKey,
              jobSortDirection,
              statusFilterKey,
              pipelineFilterKey,
              stageFilterKey,
              [...filterJobNames].sort((left, right) => left.localeCompare(right)).join(','),
              tagFilterKey,
              dateQuickFilter,
              startedAtFilterBounds.startAt,
              startedAtFilterBounds.endAt,
            )}
            noRowsText={formatRangeWindowNoDataText(
              getEmptyStateMessage('entity.noneFound', { entity: 'jobRun' }),
              dateQuickFilter,
              false,
            )}
            columnVisibilityModel={jobColumnVisibilityModel}
            onColumnVisibilityModelChange={onJobColumnVisibilityModelChange}
            onQueryMeta={setJobMeta}
            tagOptionsOverride={tagOptions}
            preservePreviousData
          />
        </Suspense>
      ) : (
        <Suspense
          fallback={<DeferredTablePanelSkeleton embedded titleWidth={84} variant="builds" />}
        >
          <BuildsTable
            nameColumnMode="job"
            nameColumnHeaderName="Jobs"
            showStageSummaryColumn={false}
            queryFn={async (input: GridQueryInput): Promise<QueryResult<BuildDto>> => {
              if (runRows.length > 0) {
                const filteredRows = filterJobRunRowsBySearch(runRows, runSearchValue);
                return Promise.resolve(
                  buildLocalQueryResult(filteredRows, {
                    ...input,
                    sorts: [
                      {
                        field: runSortFieldMap[runSortKey],
                        direction: runSortDirection,
                      } as ApiSort,
                    ],
                  }),
                );
              }

              return await queryJobRuns(selectedProjectId, selectedOrganisationId ?? undefined, {
                ...input,
                search: runSearchValue,
                sorts: [
                  { field: runSortFieldMap[runSortKey], direction: runSortDirection } as ApiSort,
                ],
                filters: mergeFilters(
                  buildInFilter('status', filterStatuses),
                  buildInFilter('pipelineId', filterPipelineIds),
                  buildInFilter('stageName', filterStageNames),
                  buildInFilter('jobName', filterJobNames),
                  buildInFilter('tags', filterTags),
                  buildDateRangeFilters(
                    'startedAt',
                    startedAtFilterBounds.startAt,
                    startedAtFilterBounds.endAt,
                  ),
                ),
              });
            }}
            queryKey={queryKeys.dataGrid(
              gridPaths.jobRuns,
              selectedOrganisationId ?? null,
              selectedProjectId ?? null,
              runSearchValue,
              runSortKey,
              runSortDirection,
              statusFilterKey,
              pipelineFilterKey,
              stageFilterKey,
              [...filterJobNames].sort((left, right) => left.localeCompare(right)).join(','),
              tagFilterKey,
              dateQuickFilter,
              startedAtFilterBounds.startAt,
              startedAtFilterBounds.endAt,
            )}
            noRowsText={formatRangeWindowNoDataText(
              getEmptyStateMessage('entity.noneFound', { entity: 'jobRun' }),
              dateQuickFilter,
              false,
            )}
            persistKey="jobs-runs-table"
            showToolbar={false}
            showPipelineColumn
            runHrefBuilder={(row) =>
              buildJobRunDetailPath({
                jobRunId: row.id,
                pipelineId: row.pipelineId ?? null,
                stageName: row.stageName ?? null,
                jobName: row.jobName ?? row.id,
              })
            }
            columnVisibilityModel={runColumnVisibilityModel}
            onColumnVisibilityModelChange={onRunColumnVisibilityModelChange}
            onQueryMeta={setRunMeta}
            tagOptionsOverride={tagOptions}
          />
        </Suspense>
      )}
    </Panel>
  );
};
